import { Link } from "react-router-dom";
import clsx from "clsx";

import { relativeDate } from "../lib/format";
import type { Collection } from "../lib/types";
import { variationFor } from "../lib/seed";
import { ProgressBand } from "./ProgressBand";

export type CollectionCardProps = {
  collection: Collection;
  className?: string;
};

/**
 * A collection as a bound volume in the series: cover plate, spine label, and
 * the tally of how far through it you are.
 */
export function CollectionCard({ collection, className }: CollectionCardProps) {
  const variation = variationFor(collection.id);

  return (
    <article
      className={clsx(
        "sheet grain group relative flex flex-col overflow-hidden transition-shadow duration-300 hover:shadow-lifted",
        className,
      )}
    >
      <Link to={`/collections/${collection.id}`} tabIndex={-1} className="block">
        <div className="relative aspect-3/2 overflow-hidden bg-paper-deep">
          {collection.coverImageUrl ? (
            <img
              src={collection.coverImageUrl}
              alt={`Cover photograph for ${collection.title}`}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            />
          ) : (
            <div className="grain absolute inset-0 flex items-center justify-center">
              <p className="field-label-tight text-ink-soft opacity-70">No cover plate yet</p>
            </div>
          )}

          {/* The spine label, printed over the plate. */}
          <span
            className="absolute bottom-3 left-3 bg-[color-mix(in_oklab,var(--color-card)_92%,transparent)] px-2 py-1 field-label-tight text-teal shadow-sheet"
            style={{ transform: `rotate(${variation.rotation}deg)` }}
          >
            {collection.geoLabel}
          </span>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="display-5 text-[1.375rem] leading-tight text-ink">
          <Link to={`/collections/${collection.id}`} className="hover:text-ochre">
            {collection.title}
          </Link>
        </h3>

        {collection.subtitle ? (
          <p className="mt-1.5 font-prose text-[0.9375rem] leading-snug text-ink-soft">
            {collection.subtitle}
          </p>
        ) : null}

        <div className="mt-auto pt-4">
          {collection.quarryState === "pending" ? (
            <p className="field-label-tight text-ochre">Drawing up the hunt list…</p>
          ) : collection.quarryState === "failed" ? (
            <p className="field-label-tight text-rust">Hunt list needs another go</p>
          ) : (
            <ProgressBand
              found={collection.foundCount}
              total={collection.quarryCount}
              plural={collection.rankPlural}
              bonusCount={collection.bonusCount}
            />
          )}

          <p className="mt-2 font-label text-[0.625rem] tracking-widest text-ink-soft uppercase opacity-70">
            Updated {relativeDate(collection.updatedAt).toLowerCase()}
          </p>
        </div>
      </div>
    </article>
  );
}
